import { useLocale } from '@components/Contexts/LocaleContext';
import { useTailwindBreakpoint } from '@components/Contexts/TailwindBreakpointContext';
import { ChevronDown } from '@components/Icons';
import { motion, useAnimation, useInView } from 'framer-motion';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { translate2 as translate } from 'utils/translate';

const Usage = () => {
  const { screenWidth } = useTailwindBreakpoint();
  const [initWidth, setInitWidth] = useState(null);
  const [expand, setExpand] = useState(false);
  const animateChevron = useAnimation();
  const ref = useRef();
  const isInView = useInView(ref, { amount: 0.5, once: true });
  const { lang } = useLocale();

  useEffect(() => {
    setInitWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    if (screenWidth) {
      setInitWidth(null);
    }
  }, [screenWidth]);

  useEffect(() => {
    animateChevron.start({
      rotate: expand ? 180 : 0,
      transition: { duration: 0.2 },
    });
  }, [expand]);

  const expandContent = () => {
    setExpand(!expand);
  };

  return (
    <section
      id="usage"
      className={
        initWidth >= 1024 || screenWidth >= 1024 ? 'Container flex' : 'flex'
      }
    >
      <div
        ref={ref}
        className="p-4 mx-4 md:mx-12 lg:mx-2 bg-[#F8F8F8] w-full rounded"
      >
        <div className="relative flex flex-col md:flex-row">
          {usages.map((usage) => (
            <div
              key={usage.section}
              className={`md:flex-1 lg:flex-auto ${usage.customClass}`}
            >
              <div
                className={
                  initWidth >= 940 || screenWidth >= 940
                    ? 'flex items-center flex-wrap'
                    : 'flex flex-col justify-center'
                }
              >
                <div className="flex justify-center items-center mt-2">
                  {usage.icon}

                  <motion.div
                    className="text-p2Semibold text-darkBlue px-3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={
                      isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }
                    }
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    {translate(
                      <>เมื่อไหร่ที่ควรใช้ {usage.sectionTH}?</>,
                      <>When is using {usage.section} suitable?</>,
                      lang,
                    )}
                  </motion.div>
                </div>
              </div>

              <ul>
                {(lang === 'en' ? usage.detail : usage.detailTH).map(
                  (item, i) =>
                    initWidth >= 768 || screenWidth >= 768 || !item.collapse ? (
                      <motion.li
                        key={item.h2}
                        initial="initial"
                        animate="animate"
                        custom={i}
                        variants={{
                          initial: { opacity: 0 },
                          animate: (i: any) =>
                            isInView
                              ? {
                                  opacity: 1,
                                  transition: {
                                    delay: 0.35 + i * 0.08,
                                    duration: 0.5,
                                  },
                                }
                              : { opacity: 0 },
                        }}
                      >
                        <div className="pt-6 flex items-start">
                          <div className="pt-[1px]">
                            <Image
                              src="/icons/blue-success.svg"
                              alt={item.h2}
                              width={16}
                              height={16}
                              layout="fixed"
                            />
                          </div>

                          <div className="text-p3Semibold pl-2 text-[#0D375B]">
                            {item.h2}{' '}
                            <span className="text-p3Regular text-black">
                              {item.h3}
                            </span>
                          </div>
                        </div>
                      </motion.li>
                    ) : (
                      <li key={item.h2}>
                        {
                          <motion.div
                            className="relative pt-6 flex items-start overflow-hidden"
                            initial={{ height: 0 }}
                            animate={{
                              height: expand ? 'fit-content' : 0,
                            }}
                            transition={{ duration: 0.25, ease: 'easeInOut' }}
                          >
                            <div className="relative pt-[1px]">
                              <Image
                                src="/icons/blue-success.svg"
                                alt={item.h2}
                                width={16}
                                height={16}
                                layout="fixed"
                              />
                            </div>

                            <div className="relative text-p3Semibold text-[#0D375B] pl-2">
                              {item.h2}{' '}
                              <span className="text-p3Regular text-black">
                                {item.h3}
                              </span>
                            </div>
                          </motion.div>
                        }
                      </li>
                    ),
                )}
              </ul>
            </div>
          ))}
        </div>

        <div className="relative flex justify-center mt-5 md:hidden bg-[#F8F8F8]">
          <div className="flex w-fit" onClick={expandContent}>
            <div className="text-p3Regular text-black">
              {expand
                ? translate('ซ่อน', 'Hide', lang)
                : translate('อ่านเพิ่ม', 'More details', lang)}
            </div>
            <div className="h-fit w-fit">
              <motion.div animate={animateChevron}>
                <ChevronDown />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Usage;

const usages = [
  {
    icon: (
      <Image
        src="/icons/system1.svg"
        alt=""
        width={18.31}
        height={13.71}
        layout="fixed"
      />
    ),
    customClass: 'md:mr-10 lg:w-[60%]',
    section: 'Section 9',
    sectionTH: 'มาตรา 9',
    detail: [
      {
        h2: 'Suitable for low-risk, general transactions',
        h3: 'such as internal organizational documents.',
      },
      {
        h2: 'An electronic signature offers a lower level of trustworthiness compared to a digital signature.',
        h3: 'This is due to its reliance on image uploads or device-based signing, making it more susceptible to forgery and unauthorized alterations.',
      },
    ],
    detailTH: [
      {
        h2: 'เหมาะสำหรับธุรกรรมทั่วไปที่มีความเสี่ยงต่ำ',
        h3: 'เช่น เอกสารบันทึกภายในขององค์กร',
      },
      {
        h2: 'ระดับความน่าเชื่อถือต่ำกว่าลายมือชื่อดิจิทัล',
        h3: 'เนื่องจากเป็นเพียงการอัปโหลดรูปเข้าระบบหรือเซ็นชื่อผ่านอุปกรณ์ทำให้การปลอมแปลงลายมือชื่อทำได้ง่าย',
      },
    ],
  },
  {
    icon: (
      <Image
        src="/icons/system2.svg"
        alt=""
        width={18.31}
        height={18.29}
        layout="fixed"
      />
    ),
    customClass: 'mt-7 md:mt-0',
    section: 'Section 26 and 28',
    sectionTH: 'มาตรา 26 และ 28',
    detail: [
      {
        h2: 'Ideal for high-value or high-risk transactions',
        h3: 'like sales agreements, trade deals, and financial documents, ensuring security and legal enforceability.',
      },
      {
        h2: 'Enables verification of any changes to signed documents,',
        h3: 'including alterations to signatures or content.',
      },
      {
        h2: 'Implements reliable identity verification aligned with IAL 2 standards,',
        h3: 'ensuring authentication at AAL 2 level or higher to match transaction risk levels.',
        collapse: true,
      },
    ],
    detailTH: [
      {
        h2: 'เหมาะสำหรับธุรกรรมที่มีมูลค่าหรือความเสี่ยงสูง และมีภาระผูกพันตามสัญญา',
        h3: 'เช่น สัญญาซื้อขาย ข้อตกลงทางการค้า เอกสารการเงิน เป็นต้น',
      },
      {
        h2: 'สามารถตรวจสอบการเปลี่ยนแปลงของเอกสารที่ผ่านการลงลายมือชื่อได้',
        h3: 'ไม่ว่าจะเป็นการเปลี่ยนแปลงของลายมือชื่อหรือข้อความบนเอกสาร',
      },
      {
        h2: 'มีขั้นตอนการพิสูจน์ตัวตนที่น่าเชื่อถือและเหมาะสมกับระดับความเสี่ยงของธุรกรรม',
        h3: 'หรือที่ระดับ IAL 2 และมีการยืนยันตัวตนที่ระดับ AAL 2 ขึ้นไป',
        collapse: true,
      },
    ],
  },
];
